/**
 * Consumer application layout.
 *
 * Desktop: a persistent sidebar.
 * Mobile: a bottom tab bar, matching the mobile app's navigation model so the
 * two platforms feel like the same product.
 */

import { Outlet, NavLink, Link } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider.jsx';
import { useSubscription } from '../providers/SubscriptionProvider.jsx';
import { Logo, LogoMark } from '../components/brand/Logo.jsx';
import { PlanBadge } from '../components/ui/Badge.jsx';
import { ThemeToggle } from '../components/ui/ThemeToggle.jsx';
import { Button } from '../components/ui/Button.jsx';
import { GlobalSearch } from '../components/search/GlobalSearch.jsx';
import { AccountBanner } from '../components/feedback/AccountBanner.jsx';
import styles from './AppLayout.module.css';
import { config } from '../config/appConfig.js';

const ICONS = {
  home: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 8.5 10 3l7 5.5V16a1 1 0 0 1-1 1h-4v-5H8v5H4a1 1 0 0 1-1-1V8.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  search: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="m13.5 13.5 3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  library: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M4 3h3v14H4zM8.5 3h3v14h-3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="m13.4 3.9 2.9.8-3.4 12.6-2.9-.8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  playlists: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 5h10M3 9h10M3 13h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="14.5" cy="13.5" r="2.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M17 13.5V7l0 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  profile: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="7" r="3.2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4 16.5a6 6 0 0 1 12 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  /* A tuning fork: an object, like the metronome for Learn and the books for
     Library. A gauge was the obvious alternative and was rejected — it repeats
     the meter drawn inside the screen, and at 20px a needle reads as a
     speedometer, which is to say "analytics". Matches the mobile tab icon. */
  tuner: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M7 3v6a3 3 0 0 0 6 0V3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M10 12v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M8.2 17h3.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  heart: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 16s-6-3.8-6-7.7A3.3 3.3 0 0 1 10 6a3.3 3.3 0 0 1 6 2.3C16 12.2 10 16 10 16Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  clock: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 6v4.2l2.6 1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  download: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 3v9m0 0 3.2-3.2M10 12 6.8 8.8M4 15.5h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  note: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="7" cy="14" r="2.6" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9.6 14V4.5l6 -1.3V12" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="13" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),
  /* A metronome: practice and motion, rather than the graduation cap every
     other learning product uses. Matches the mobile tab icon. */
  learn: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M7.6 3h4.8l2.6 14H5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M6.4 12.4h7.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M13.2 5.4 8.4 13.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
};

const PRIMARY_NAV = [
  { to: '/app', label: 'Home', icon: ICONS.home, end: true },
  { to: '/app/search', label: 'Search', icon: ICONS.search },
  { to: '/app/learn', label: 'Learn', icon: ICONS.learn },
  // Next to Learn: both are things you do to get better at playing, rather
  // than places songs live.
  { to: '/app/tuner', label: 'Tuner', icon: ICONS.tuner },
  { to: '/app/library', label: 'Library', icon: ICONS.library },
  { to: '/app/playlists', label: 'Playlists', icon: ICONS.playlists },
];

const LIBRARY_NAV = [
  { to: '/app/favorites', label: 'Favorites', icon: ICONS.heart },
  { to: '/app/recent', label: 'Recently played', icon: ICONS.clock },
  { to: '/app/my-songs', label: 'My songs', icon: ICONS.note },
  { to: '/app/downloads', label: 'Downloads', icon: ICONS.download },
];

/* Mobile shows five tabs, mirroring the mobile app — Learn in the centre. */
/*
 * Five tabs, mirroring the mobile app — and Tuner takes the slot Profile used
 * to hold rather than becoming a sixth.
 *
 * `.tab { flex: 1 }` divides the bar evenly, so a sixth item would shrink every
 * label past legibility at 375px; on mobile it would also cost Learn the centre
 * position, which that layout treats as deliberate. Profile is a better thing
 * to give up: it is already absent from the desktop sidebar's primary nav, and
 * as an avatar in the top bar it is now reachable from all five tabs instead of
 * only from one.
 */
const MOBILE_TABS = [
  { to: '/app', label: 'Home', icon: ICONS.home, end: true },
  { to: '/app/search', label: 'Search', icon: ICONS.search },
  { to: '/app/learn', label: 'Learn', icon: ICONS.learn },
  { to: '/app/library', label: 'Library', icon: ICONS.library },
  { to: '/app/tuner', label: 'Tuner', icon: ICONS.tuner },
];

export function AppLayout() {
  const { profile, isModerator } = useAuth();
  const { plan, status, isPro } = useSubscription();

  return (
    <div className={styles.shell}>
      <a href="#app-main" className="skip-link">
        Skip to content
      </a>

      <aside className={styles.sidebar}>
        <Link to="/app" className={styles.sidebarLogo} aria-label={`${config.appName} home`}>
          <Logo />
        </Link>

        <nav className={styles.nav} aria-label="Main">
          {PRIMARY_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navActive : ''}`}
            >
              <span className={styles.navIcon}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}

          <p className={styles.navHeading}>Your library</p>

          {LIBRARY_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navActive : ''}`}
            >
              <span className={styles.navIcon}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          {!isPro && (
            <div className={styles.upgradeCard}>
              <p className={styles.upgradeTitle}>Unlock unlimited</p>
              <p className={styles.upgradeText}>
                Unlimited playlists, songs and offline access with Premium.
              </p>
              <Button to="/app/upgrade" variant="primary" size="sm" fullWidth>
                See Premium
              </Button>
            </div>
          )}

          {isModerator && (
            <NavLink to="/admin" className={styles.navItem}>
              <span className={styles.navIcon}>{ICONS.profile}</span>
              Admin
            </NavLink>
          )}

          <NavLink
            to="/app/settings"
            className={({ isActive }) => `${styles.accountLink} ${isActive ? styles.navActive : ''}`}
          >
            <span className={styles.avatar} aria-hidden="true">
              {profile?.photoURL ? (
                <img src={profile.photoURL} alt="" className={styles.avatarImage} />
              ) : (
                (profile?.displayName ?? '?').charAt(0).toUpperCase()
              )}
            </span>
            <span className={styles.accountText}>
              <span className={styles.accountName}>{profile?.displayName ?? 'Account'}</span>
              <PlanBadge plan={plan} status={status?.status} />
            </span>
          </NavLink>
        </div>
      </aside>

      <div className={styles.content}>
        <header className={styles.topBar}>
          <Link to="/app" className={styles.mobileLogo} aria-label={`${config.appName} home`}>
            <LogoMark size={26} />
          </Link>

          <GlobalSearch />

          <div className={styles.topBarActions}>
            {/*
             * The account entry point on mobile, where Profile is no longer a
             * tab. Hidden on desktop, which already has the sidebar footer link.
             */}
            <NavLink
              to="/app/settings"
              className={styles.accountTab}
              aria-label="Your profile and settings"
            >
              <span className={styles.avatar} aria-hidden="true">
                {profile?.photoURL ? (
                  <img src={profile.photoURL} alt="" className={styles.avatarImage} />
                ) : (
                  (profile?.displayName ?? '?').charAt(0).toUpperCase()
                )}
              </span>
            </NavLink>
            <ThemeToggle />
          </div>
        </header>

        <AccountBanner />

        <main id="app-main" className={styles.main}>
          <Outlet />
        </main>
      </div>

      <nav className={styles.tabBar} aria-label="Main">
        {MOBILE_TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span className={styles.tabLabel}>{tab.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
